/* Internal cart-link generator: builds a shareable URL that clears a visitor's cart,
   switches their market/country, adds selected items (with selling plans) and discount
   codes, then sends them straight to checkout. */
(function () {
  'use strict';

  var root = document.querySelector('[data-cartgen]');
  if (!root) return;

  var CFG = window.__cartgenConfig || { products: [] };
  var CURRENT_COUNTRY = window.__cartgenCountry || '';
  var STORAGE_KEY = 'cartgen_state_v1';
  var LOADER_KEY = 'cartgen_loader_v1';

  function readState() {
    try { return JSON.parse(sessionStorage.getItem(STORAGE_KEY)) || { items: {}, discounts: [], country: CURRENT_COUNTRY }; }
    catch (e) { return { items: {}, discounts: [], country: CURRENT_COUNTRY }; }
  }
  function writeState(state) { sessionStorage.setItem(STORAGE_KEY, JSON.stringify(state)); }

  /* ---------------- Loader mode: a shared link was opened ---------------- */
  function base64UrlDecode(str) {
    str = str.replace(/-/g, '+').replace(/_/g, '/');
    while (str.length % 4) str += '=';
    return decodeURIComponent(escape(window.atob(str)));
  }
  function base64UrlEncode(str) {
    return window.btoa(unescape(encodeURIComponent(str))).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
  }

  function getUrlPayload() {
    var params = new URLSearchParams(window.location.search);
    var raw = params.get('cart');
    if (!raw) return null;
    try { return JSON.parse(base64UrlDecode(raw)); } catch (e) { return null; }
  }

  function getLoaderPayload() {
    try { return JSON.parse(sessionStorage.getItem(LOADER_KEY)); } catch (e) { return null; }
  }

  function setLoaderPayload(payload) { sessionStorage.setItem(LOADER_KEY, JSON.stringify(payload)); }
  function clearLoaderPayload() { sessionStorage.removeItem(LOADER_KEY); }

  function showLoaderUI(message) {
    root.innerHTML =
      '<div class="cartgen__loader"><div class="cartgen__spinner"></div><p>' + escapeHtml(message) + '</p>' +
      '<p class="cartgen__error" data-cartgen-error hidden></p></div>';
  }

  function showLoaderError(message, fallbackHref) {
    var errEl = root.querySelector('[data-cartgen-error]');
    if (errEl) {
      errEl.hidden = false;
      errEl.innerHTML = escapeHtml(message) + (fallbackHref ? ' <a href="' + fallbackHref + '">Go to checkout anyway</a>' : '');
    }
  }

  function escapeHtml(str) {
    var div = document.createElement('div');
    div.textContent = str;
    return div.innerHTML;
  }

  function submitCountrySwitch(countryIso) {
    var form = document.getElementById('CartGenCountryForm');
    if (!form) return false;
    var input = form.querySelector('input[name="country_code"]');
    if (!input) return false;
    input.value = countryIso;
    form.submit();
    return true;
  }

  function runLoader(payload) {
    // A new cart link must start from a clean slate, not whatever was previously builder-side selected.
    writeState({ items: {}, discounts: [], country: payload.country || CURRENT_COUNTRY });

    // Persist across the (possible) full-page reload triggered by a market/country switch.
    setLoaderPayload(payload);

    if (payload.country && payload.country !== CURRENT_COUNTRY) {
      showLoaderUI('Switching to the ' + payload.country + ' market\u2026');
      if (submitCountrySwitch(payload.country)) return; // page will reload
    }

    showLoaderUI('Preparing your cart\u2026');
    processCart(payload)
      .then(function () {
        clearLoaderPayload();
        window.location.href = '/checkout';
      })
      .catch(function (err) {
        console.error('[cartgen] failed', err);
        showLoaderError('Something went wrong building the cart: ' + (err && err.message ? err.message : err), '/checkout');
      });
  }

  function processCart(payload) {
    return fetch('/cart/clear.js', { method: 'POST' })
      .then(function () {
        if (!payload.items || !payload.items.length) return;
        var formData = new FormData();
        payload.items.forEach(function (item, i) {
          formData.append('items[' + i + '][id]', item.id);
          formData.append('items[' + i + '][quantity]', item.qty);
          if (item.sellingPlan) formData.append('items[' + i + '][selling_plan]', item.sellingPlan);
        });
        return fetch('/cart/add.js', { method: 'POST', headers: { Accept: 'application/json' }, body: formData })
          .then(function (res) { return res.json().then(function (data) { return { ok: res.ok, data: data }; }); })
          .then(function (r) {
            if (!r.ok) throw new Error((r.data && (r.data.description || r.data.message)) || 'Could not add items to cart.');
          });
      })
      .then(function () {
        // A discount code applied by a previous cart survives /cart/clear.js, so always clear it
        // first, then only re-apply the codes (if any) that belong to this new cart.
        var chain = fetch('/discount/');
        (payload.discounts || []).forEach(function (code) {
          chain = chain.then(function () { return fetch('/discount/' + encodeURIComponent(code)); });
        });
        return chain;
      });
  }

  /* On load: resume an in-progress loader run (after a country-switch reload), or start one
     from the ?cart= URL param, otherwise render the normal builder UI. */
  var resumePayload = getLoaderPayload();
  var urlPayload = getUrlPayload();
  if (resumePayload) { runLoader(resumePayload); return; }
  if (urlPayload) { runLoader(urlPayload); return; }

  /* ---------------- Builder mode: internal team UI ---------------- */
  var state = readState();

  function persist() { writeState(state); }

  function money(amount) {
    return (window.__cartgenCurrency || '$') + Number(amount || 0).toFixed(2);
  }

  function buildProductListHtml() {
    return CFG.products.map(function (p) {
      var rows = p.variants.map(function (v) {
        var itemState = state.items[v.id] || { qty: 0, sellingPlan: 0 };
        /* Subscription-only variants can't be added with selling_plan=0, so never offer that option for them,
           and default the stored state to the first plan so it matches what the <select> shows on load. */
        if (v.requiresSellingPlan && !itemState.sellingPlan && v.plans.length) {
          itemState.sellingPlan = v.plans[0].id;
          state.items[v.id] = itemState;
        }
        var oneTimeOption = v.requiresSellingPlan ? '' : '<option value="0">One-time purchase</option>';
        var planOptions = oneTimeOption + v.plans.map(function (plan) {
          return '<option value="' + plan.id + '"' + (itemState.sellingPlan == plan.id ? ' selected' : '') + '>' + escapeHtml(plan.name) + '</option>';
        }).join('');
        return (
          '<div class="cartgen__variant" data-cartgen-variant-row data-search="' + escapeHtml((p.title + ' ' + v.title).toLowerCase()) + '">' +
            '<span class="cartgen__variant-name">' + escapeHtml(v.title) + (v.available ? '' : ' (unavailable)') + '</span>' +
            '<span class="cartgen__variant-price">' + escapeHtml(v.priceFormatted) + '</span>' +
            '<input type="number" min="0" class="cartgen__variant-qty" data-cartgen-qty data-variant-id="' + v.id + '" value="' + (itemState.qty || 0) + '">' +
            (v.plans.length ? '<select class="cartgen__variant-plan" data-cartgen-plan data-variant-id="' + v.id + '">' + planOptions + '</select>' : '') +
          '</div>'
        );
      }).join('');
      return '<div class="cartgen__product" data-cartgen-product data-search="' + escapeHtml(p.title.toLowerCase()) + '">' +
        '<div class="cartgen__product-title">' + escapeHtml(p.title) + '</div>' + rows + '</div>';
    }).join('');
  }

  function buildCountryOptionsHtml() {
    return (CFG.countries || []).map(function (c) {
      return '<option value="' + c.iso + '"' + (c.iso === CURRENT_COUNTRY ? ' selected' : '') + '>' + escapeHtml(c.name) + '</option>';
    }).join('');
  }

  root.innerHTML =
    (CFG.countries && CFG.countries.length <= 1
      ? '<p class="cartgen__notice">Only one market is currently configured for this store, so the country selector below won\u2019t change pricing until additional Shopify Markets are set up.</p>'
      : '') +
    '<div class="cartgen__row">' +
      '<div class="cartgen__field">' +
        '<label for="cartgen-country">Country / market</label>' +
        '<select id="cartgen-country" data-cartgen-country>' + buildCountryOptionsHtml() + '</select>' +
      '</div>' +
      '<div class="cartgen__field">' +
        '<label for="cartgen-discounts">Discount code(s)</label>' +
        '<input type="text" id="cartgen-discounts" data-cartgen-discounts placeholder="CODE1, CODE2" value="' + escapeHtml((state.discounts || []).join(', ')) + '">' +
      '</div>' +
    '</div>' +
    '<input type="search" class="cartgen__search" data-cartgen-search placeholder="Search products or variants\u2026">' +
    '<div data-cartgen-list>' + buildProductListHtml() + '</div>' +
    '<div class="cartgen__summary">' +
      '<span data-cartgen-count>0 items selected</span>' +
      '<strong data-cartgen-subtotal>' + money(0) + '</strong>' +
      '<button type="button" class="cartgen__generate-btn" data-cartgen-generate>Generate Link</button>' +
    '</div>' +
    '<div class="cartgen__link-output cartgen__hidden" data-cartgen-output-wrap>' +
      '<input type="text" readonly data-cartgen-output>' +
      '<button type="button" class="cartgen__copy-btn" data-cartgen-copy>Copy</button>' +
    '</div>';

  var listEl = root.querySelector('[data-cartgen-list]');
  var countEl = root.querySelector('[data-cartgen-count]');
  var subtotalEl = root.querySelector('[data-cartgen-subtotal]');
  var outputWrap = root.querySelector('[data-cartgen-output-wrap]');
  var outputInput = root.querySelector('[data-cartgen-output]');

  function variantById(id) {
    for (var i = 0; i < CFG.products.length; i++) {
      var v = CFG.products[i].variants.filter(function (x) { return String(x.id) === String(id); })[0];
      if (v) return v;
    }
    return null;
  }

  function refreshSummary() {
    var count = 0, subtotal = 0;
    Object.keys(state.items).forEach(function (id) {
      var item = state.items[id];
      if (!item.qty) return;
      var v = variantById(id);
      if (!v) return;
      count += item.qty;
      subtotal += item.qty * v.price;
    });
    countEl.textContent = count + ' item' + (count === 1 ? '' : 's') + ' selected';
    subtotalEl.textContent = money(subtotal);
  }

  listEl.addEventListener('input', function (event) {
    var qtyEl = event.target.closest('[data-cartgen-qty]');
    var planEl = event.target.closest('[data-cartgen-plan]');
    if (qtyEl) {
      var id = qtyEl.getAttribute('data-variant-id');
      var qty = Math.max(0, parseInt(qtyEl.value, 10) || 0);
      state.items[id] = state.items[id] || { qty: 0, sellingPlan: 0 };
      state.items[id].qty = qty;
      persist();
      refreshSummary();
    }
    if (planEl) {
      var pid = planEl.getAttribute('data-variant-id');
      state.items[pid] = state.items[pid] || { qty: 0, sellingPlan: 0 };
      state.items[pid].sellingPlan = parseInt(planEl.value, 10) || 0;
      persist();
    }
  });

  var searchEl = root.querySelector('[data-cartgen-search]');
  searchEl.addEventListener('input', function () {
    var term = searchEl.value.trim().toLowerCase();
    root.querySelectorAll('[data-cartgen-product]').forEach(function (productEl) {
      var rows = productEl.querySelectorAll('[data-cartgen-variant-row]');
      var productMatches = productEl.getAttribute('data-search').indexOf(term) !== -1;
      var anyRowVisible = false;
      rows.forEach(function (row) {
        var visible = productMatches || row.getAttribute('data-search').indexOf(term) !== -1;
        row.classList.toggle('cartgen__hidden', !visible);
        if (visible) anyRowVisible = true;
      });
      productEl.classList.toggle('cartgen__hidden', !anyRowVisible);
    });
  });

  var discountsEl = root.querySelector('[data-cartgen-discounts]');
  discountsEl.addEventListener('input', function () {
    state.discounts = discountsEl.value.split(',').map(function (s) { return s.trim(); }).filter(Boolean);
    persist();
  });

  var countryEl = root.querySelector('[data-cartgen-country]');
  countryEl.addEventListener('change', function () {
    persist();
    submitCountrySwitch(countryEl.value);
  });

  root.querySelector('[data-cartgen-generate]').addEventListener('click', function () {
    var items = Object.keys(state.items)
      .map(function (id) { return { id: Number(id), qty: state.items[id].qty, sellingPlan: state.items[id].sellingPlan || 0 }; })
      .filter(function (item) { return item.qty > 0; });
    if (!items.length) { window.alert('Select at least one item first.'); return; }
    var payload = { items: items, discounts: state.discounts || [], country: countryEl.value };
    var encoded = base64UrlEncode(JSON.stringify(payload));
    var url = window.location.origin + window.location.pathname + '?cart=' + encoded;
    outputInput.value = url;
    outputWrap.classList.remove('cartgen__hidden');
    outputInput.focus();
    outputInput.select();
  });

  root.querySelector('[data-cartgen-copy]').addEventListener('click', function () {
    outputInput.select();
    if (navigator.clipboard && navigator.clipboard.writeText) navigator.clipboard.writeText(outputInput.value);
    else document.execCommand('copy');
  });

  refreshSummary();
})();
